import { APIClient } from "../helpers/api_helper";
import type { DepositCollectionMethod } from "./paymentMethodSettingsService";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

const api = new APIClient();

const RESOURCE = "/appointments";

export interface AppointmentClient {
  id: number;
  name: string;
  health_records_count?: number;
  /** Operational client notes (GAP-F2-126). Distinct from appointment.notes. */
  notes?: string | null;
  /** Structured prefs (GAP-F2-065). Distinct from notes / tags. */
  service_preferences?: import("../helpers/clientServicePreferences").ClientServicePreferences | null;
}

export interface AppointmentServiceSummary {
  id: number;
  name: string;
  duration_minutes?: number;
  buffer_minutes?: number;
  price?: number;
  require_deposit?: boolean;
  client_record_template_id?: number | null;
}

export interface AppointmentNotificationReminder {
  id: number;
  reminder_type: "created" | "reminder" | string;
  channel: "whatsapp" | "email" | string;
  hours_before: number | null;
  delivery_mode: "pooled" | "tenant_number" | null;
  sent_at: string | null;
}

export interface AppointmentProfessional {
  id: number;
  name: string;
  color_hex?: string;
}

export interface AppointmentUnit {
  id: number;
  name: string;
}

export interface AppointmentPixPayload {
  qr_code_base64: string;
  copy_paste_string: string;
}

export type { DepositCollectionMethod };
export { DEPOSIT_COLLECTION_METHOD_KEYS as DEPOSIT_COLLECTION_METHOD_OPTIONS } from "./paymentMethodSettingsService";

export const isManualPendingDeposit = (appointment: Appointment): boolean =>
  appointment.status === "pending_deposit" &&
  appointment.deposit_collection_method !== null &&
  appointment.deposit_collection_method !== "gateway_pix";

export const isGatewayPixPendingDeposit = (appointment: Appointment): boolean =>
  appointment.status === "pending_deposit" &&
  appointment.deposit_status === "pending" &&
  appointment.deposit_collection_method === "gateway_pix";

export interface AppointmentExtraService {
  id: number;
  sort_order: number;
  name?: string | null;
  duration_minutes?: number | null;
  buffer_minutes?: number | null;
  price?: number | null;
}

export interface Appointment {
  id: number;
  tenant_id: number;
  unit_id: number;
  professional_id: number;
  client_id: number;
  service_id: number | null;
  service_bundle_id?: number | null;
  bundle_group_id?: string | null;
  bundle_item_sort_order?: number | null;
  start_datetime: string;
  end_datetime: string;
  status: string;
  status_note?: string | null;
  /** Observação operacional do horário (GAP-F2-087). Distinta de clients.notes / execution_notes / status_note. */
  notes?: string | null;
  deposit_percent_bps: number | null;
  deposit_amount: number | null;
  deposit_status: string | null;
  deposit_external_transaction_id?: string | null;
  deposit_collection_method: DepositCollectionMethod | null;
  deposit_collection_note: string | null;
  deposit_confirmed_at: string | null;
  deposit_confirmed_by_user_id: number | null;
  pix?: AppointmentPixPayload;
  client?: AppointmentClient;
  professional?: AppointmentProfessional;
  service?: AppointmentServiceSummary;
  /** Complementos avulsos (não é combo). */
  extra_services?: AppointmentExtraService[];
  unit?: AppointmentUnit;
  /** Hint: client has active package sessions for this service (not a reservation). */
  has_package_credit?: boolean;
  package_credit_sessions?: number;
  /** Hint: client has active B2C club membership. */
  has_active_club?: boolean;
  club_plan_name?: string | null;
  club_visits_remaining?: number | null;
  /** GAP-F2-052 — missing ficha for current scope (client or client+service). */
  needs_health_record?: boolean;
  /** GAP-F2-053 polish — ficha with legal blocks still missing digital consent. */
  needs_consent?: boolean;
  /** GAP-F2-120 — positive clinical gate answer linked to service/product. */
  has_clinical_gate_alert?: boolean;
  clinical_gate_alerts?: Array<{
    field_slug: string;
    label: string;
    answer_label: string;
    matched_service_ids?: number[];
    matched_product_ids?: number[];
  }>;
  has_evolution?: boolean;
  evolution_record_id?: number | null;
  can_register_evolution?: boolean;
  /** ADJ-F3.5-002 — fotos da evolução perto do purge (retenção − 7d). */
  evolution_photos_expiring_soon?: boolean;
  evolution_photos_expire_in_days?: number | null;
  /** ISO-8601 when reception marked the client as arrived (balcão). */
  client_arrived_at?: string | null;
  /** Sala de espera pós-Chegou (GAP-F2-113). */
  waiting_room_status?: "waiting" | "called" | "in_service" | null;
  waiting_room_called_at?: string | null;
  waiting_room_started_at?: string | null;
  notify_on_created?: boolean;
  notify_reminder?: boolean;
  notification_reminders?: AppointmentNotificationReminder[];
  created_at: string;
  updated_at: string;
}

export interface AvailabilitySlot {
  start_datetime: string;
  end_datetime: string;
}

export interface CreateAppointmentPayload {
  unit_id: number;
  professional_id: number;
  client_id: number;
  service_id: number;
  extra_service_ids?: number[];
  notes?: string | null;
  start_datetime: string;
  end_datetime: string;
  deposit_percent_bps?: number;
  deposit_collection_method?: DepositCollectionMethod;
  deposit_collection_note?: string;
  notify_on_created?: boolean;
  notify_reminder?: boolean;
  acknowledge_clinical_gate?: boolean;
}

export interface ConfirmDepositPayload {
  deposit_collection_note?: string;
}

export interface AppointmentListParams extends PaginationParams {
  unit_id?: number;
  professional_id?: number;
  client_id?: number;
  status?: string;
  date?: string;
  start_date?: string;
  end_date?: string;
}

export interface AvailabilityParams {
  date: string;
  service_id: number;
  extra_service_ids?: number[];
}

export interface UpdateAppointmentPayload {
  status?: string;
  professional_id?: number;
  service_id?: number;
  extra_service_ids?: number[];
  notes?: string | null;
  start_datetime?: string;
  client_arrived_at?: string | null;
  notify_on_created?: boolean;
  notify_reminder?: boolean;
  acknowledge_clinical_gate?: boolean;
}

export interface UpdateAppointmentStatusPayload {
  status: string;
}

/** PATCH envelope — `order_id` at root when status becomes `completed` (opens comanda). */
export interface UpdateAppointmentStatusResponse extends ApiResponse<Appointment> {
  order_id?: number;
}

export const update = (
  appointmentId: number,
  payload: UpdateAppointmentPayload
): Promise<UpdateAppointmentStatusResponse> =>
  api.update<UpdateAppointmentStatusResponse>(`${RESOURCE}/${appointmentId}`, payload);

export const updateStatus = (
  appointmentId: number,
  payload: UpdateAppointmentStatusPayload
): Promise<UpdateAppointmentStatusResponse> =>
  update(appointmentId, payload);

export const markClientArrived = (
  appointmentId: number,
  options?: { acknowledge_clinical_gate?: boolean }
): Promise<UpdateAppointmentStatusResponse> =>
  update(appointmentId, {
    client_arrived_at: new Date().toISOString(),
    ...(options?.acknowledge_clinical_gate
      ? { acknowledge_clinical_gate: true }
      : {}),
  });

export const clearClientArrived = (
  appointmentId: number
): Promise<UpdateAppointmentStatusResponse> =>
  update(appointmentId, { client_arrived_at: null });

export const getAll = (
  params?: AppointmentListParams
): Promise<PaginatedApiResponse<Appointment>> =>
  api.get<PaginatedApiResponse<Appointment>>(RESOURCE, params);

export const getById = (appointmentId: number): Promise<ApiResponse<Appointment>> =>
  api.get<ApiResponse<Appointment>>(`${RESOURCE}/${appointmentId}`);

export const listAppointments = getAll;

export const create = (
  payload: CreateAppointmentPayload
): Promise<ApiResponse<Appointment>> =>
  api.create<ApiResponse<Appointment>>(RESOURCE, payload);

export const confirmDeposit = (
  appointmentId: number | string,
  payload?: ConfirmDepositPayload
): Promise<ApiResponse<Appointment>> =>
  api.create<ApiResponse<Appointment>>(
    `${RESOURCE}/${appointmentId}/confirm-deposit`,
    payload ?? {}
  );

export type DepositPixChannel = "email" | "whatsapp";

export type DepositPixChannelResult = {
  status: "queued" | "skipped" | "rate_limited" | string;
  code?: string;
  message?: string;
};

export type SendDepositPixPayload = {
  channels: DepositPixChannel[];
};

export type SendDepositPixResult = {
  channels: Partial<Record<DepositPixChannel, DepositPixChannelResult>>;
};

export const sendDepositPix = (
  appointmentId: number | string,
  payload: SendDepositPixPayload
): Promise<ApiResponse<SendDepositPixResult>> =>
  api.create<ApiResponse<SendDepositPixResult>>(
    `${RESOURCE}/${appointmentId}/send-deposit-pix`,
    payload
  );

export type DepositPixPayloadResponse = {
  pix: AppointmentPixPayload;
};

export const getDepositPix = (
  appointmentId: number | string
): Promise<ApiResponse<DepositPixPayloadResponse>> =>
  api.get<ApiResponse<DepositPixPayloadResponse>>(
    `${RESOURCE}/${appointmentId}/deposit-pix`
  );

export const getAvailability = (
  professionalId: number,
  params: AvailabilityParams
): Promise<ApiResponse<AvailabilitySlot[]>> => {
  const search = new URLSearchParams();
  search.set("date", params.date);
  search.set("service_id", String(params.service_id));
  (params.extra_service_ids ?? []).forEach((id) => {
    search.append("extra_service_ids[]", String(id));
  });

  return api.get<ApiResponse<AvailabilitySlot[]>>(
    `/professionals/${professionalId}/availability?${search.toString()}`
  );
};
